import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import type { TransformedTarget } from '../../../../../api/targets-client/targets-client.type';
import { useTranslation } from 'react-i18next';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import { toggleTargetSelection } from '../../../../../store/process-analysis-store/process-analysis-store.actions';
import { skyLightest } from '../../../../../styles/design-tokens';
import { Checkbox } from '../../../../../ui-library/checkbox/checkbox';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Text } from '../../../../../ui-library/typography/typography';
import { formatTargetDateRange, getTargetComparisonSymbol } from './side-section.helpers';

type Props = {
  target: TransformedTarget;
  measureId: string;
};

export const SideSectionTargetsItem = ({ target, measureId }: Props) => {
  const { t } = useTranslation();
  const selectedTargets = useProcessAnalysisStore((state) => state.selectedTargets);

  const isSelected = selectedTargets.some(
    (selectedTarget) => selectedTarget.measureId === measureId && selectedTarget.targetId === target.id,
  );

  const handleToggle = () => {
    toggleTargetSelection({
      measureId,
      targetId: target.id,
    });
  };
  const targetSymbol = getTargetComparisonSymbol(
    target.target_comparison,
    target.target_value,
    target.target_value_upper,
  );

  const dateRange = formatTargetDateRange(target.start_date, target.end_date, t);

  return (
    <Container>
      <Group gap={8}>
        <Checkbox size="xs" checked={isSelected} onChange={handleToggle} style={{ borderRadius: 2 }} />
        <Text size="tiny">{t('process_analysis.side_section.show_target_on_graph')}</Text>
      </Group>

      {target.name && (
        <Text size="tiny" weight="medium">
          {target.name}
        </Text>
      )}

      <Divider />

      <Stack gap={4}>
        <Group gap={8} justify="space-between">
          <Text size="tiny" color="inkLight">
            {t('process_analysis.side_section.target_value_label')}
          </Text>
          <Text size="tiny">{targetSymbol}</Text>
        </Group>

        <Group gap={8} justify="space-between">
          <Text size="tiny" color="inkLight">
            {t('process_analysis.side_section.target_frequency_label')}
          </Text>
          <Text size="tiny">{target.time_allocation_type}</Text>
        </Group>

        <Group gap={8} justify="space-between">
          <Text size="tiny" color="inkLight">
            {t('process_analysis.side_section.date_label')}
          </Text>
          <Text size="tiny">{dateRange}</Text>
        </Group>
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${skyLightest};
  border-radius: 8px;
`;
